'use client';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';
import { peut } from '@/lib/meta';
import { I, card, inputStyle, FieldError, useBusy } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
/* ─────────── Catalogue des règles CQI — création et activation ───────────
 * Dernière brique de C3. Une règle n'est pas du code : elle se décrit par
 * cinq paramètres, et le moteur (backend services/westgard.ts) ne connaît que
 * quatre opérateurs. Ajouter `8-1s` ou `3-2s` ne demande donc aucune reprise.
 *
 * Le laboratoire n'applique aujourd'hui que les six règles classiques. Cet
 * écran existe pour le jour où ce ne sera plus vrai — et pour désactiver
 * proprement celles qu'il ne retient pas.
 * ------------------------------------------------------------------------ */

type Regle = {
  documentId: string; code: string; libelle: string; description: string;
  n: number; seuil: number; operateur: string; portee: string; gravite: string;
  standard: boolean; actif: boolean;
};

const OPERATEURS: { cle: string; nom: string; aide: string }[] = [
  { cle: 'depasse', nom: 'dépasse le seuil', aide: 'Le point dépasse ±seuil σ. Un seul point à la fois.' },
  { cle: 'meme_cote', nom: 'n points du même côté', aide: 'Les n derniers points sont tous du même côté de la cible ET au-delà du seuil. Avec un seuil de 0, seul le côté compte — c’est la règle de tendance 10-x.' },
  { cle: 'amplitude', nom: 'amplitude entre niveaux', aide: 'L’écart entre deux niveaux d’une même série atteint le seuil. C’est la logique de R-4s.' },
  { cle: 'tendance', nom: 'n points monotones', aide: 'n points consécutivement croissants ou décroissants, quel que soit leur écart à la cible. N’existe pas chez Westgard.' },
];

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4,
};

export function CatalogueRegles() {
  const { session, settings } = useGed();
  const autorise = peut(settings?.perms, session.profile, 'cqiRegles');

  const [regles, setRegles] = useState<Regle[]>([]);
  const [ouvert, setOuvert] = useState(false);
  // Lot 3 (11/08/2026) — une règle PERSONNALISÉE se corrige (même formulaire,
  // code figé). Les règles standard, elles, ne se réécrivent pas : leur code
  // est cité par l'historique des rejets, et « 2-2s » doit toujours vouloir
  // dire la même chose qu'à Westgard.
  const [editionCode, setEditionCode] = useState<string | null>(null);
  const [f, setF] = useState({ code: '', libelle: '', description: '', n: '2', seuil: '2', operateur: 'meme_cote', portee: 'niveau', gravite: 'rejet' });
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const { run, estOccupe } = useBusy();

  const charger = useCallback(async () => {
    const r = await fetch('/api/ged/ged-regle-cqis?sort=code:asc&pagination[pageSize]=100', { credentials: 'include' });
    if (!r.ok) return;
    const d = await r.json();
    setRegles((d.data || []) as Regle[]);
  }, []);
  // `.then()` et non `await` : une fonction `async` appelée directement dans
  // l'effet masque le fait que les `setState` suivent le premier await, ce que
  // React 19 signale comme un rendu en cascade.
  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged-regle-cqis?sort=code:asc&pagination[pageSize]=100', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((d) => { if (vivant) setRegles((d.data || []) as Regle[]); })
      .catch(() => undefined);
    return () => { vivant = false; };
  }, []);

  if (!autorise) return null;

  const envoyer = async (corps: Record<string, unknown>) => {
    const r = await fetchEcriture('/api/ged/ged/cqi-regles', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corps),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setErreur(d?.error?.message || 'Enregistrement refusé.');
      return false;
    }
    setErreur('');
    await charger();
    return true;
  };

  const creer = async () => {
    const code = f.code.trim();
    if (!code) { setErreur('Le code est obligatoire — c’est lui qui apparaît sur les points rejetés.'); return; }
    if (!editionCode && regles.some((r) => r.code === code)) { setErreur(`Le code « ${code} » existe déjà.`); return; }
    const ok = await envoyer({
      code, libelle: f.libelle.trim() || code, description: f.description.trim(),
      n: parseInt(f.n, 10), seuil: parseFloat(f.seuil.replace(',', '.')),
      operateur: f.operateur, portee: f.portee, gravite: f.gravite,
    });
    if (ok) {
      setMessage(editionCode
        ? `Règle ${code} corrigée — elle s’applique telle quelle aux cartes qui la cochent.`
        : `Règle ${code} créée. Activez-la carte par carte pour qu’elle s’applique.`);
      setOuvert(false);
      setEditionCode(null);
      setF({ code: '', libelle: '', description: '', n: '2', seuil: '2', operateur: 'meme_cote', portee: 'niveau', gravite: 'rejet' });
    }
  };

  const aide = OPERATEURS.find((o) => o.cle === f.operateur)?.aide || '';

  return (
    <div style={{ ...card, padding: 16, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14, color: 'var(--text-strong)' }}>Catalogue des règles</strong>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {regles.length} règle(s) · {regles.filter((r) => r.actif).length} active(s)
        </span>
        <span style={{ flex: 1 }} />
        {!ouvert && (
          <Bouton taille="sm" onClick={() => { setOuvert(true); setMessage(''); }}>
            <I n="plus" s={13} />Nouvelle règle
          </Bouton>
        )}
      </div>

      <div style={{ display: 'grid', gap: 6 }}>
        {regles.map((r) => (
          <div key={r.documentId} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '7px 10px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', opacity: r.actif ? 1 : 0.55 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, fontFamily: 'var(--font-mono)', minWidth: 54 }}>{r.code}</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-body)', flex: '1 1 240px' }}>{r.libelle}</span>
            <span style={{ fontSize: 10.5, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
              n={r.n} · {r.seuil}σ · {r.operateur} · {r.portee === 'inter_niveaux' ? 'inter-niveaux' : 'niveau'}
            </span>
            <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: r.gravite === 'rejet' ? 'var(--red-50)' : 'var(--amber-50)', color: r.gravite === 'rejet' ? 'var(--red-600)' : 'var(--amber-600)' }}>
              {r.gravite === 'rejet' ? 'rejet' : 'alerte'}
            </span>
            {r.standard ? (
              <span title="Règle de Westgard livrée d’origine : désactivable, mais ni supprimable ni modifiable — l’historique des rejets référence son code, et « 2-2s » doit garder son sens."
                style={{ fontSize: 12.5, color: 'var(--text-muted)', cursor: 'help' }}>standard</span>
            ) : (
              <Bouton taille="sm" onClick={() => {
                setEditionCode(r.code); setOuvert(true); setMessage(''); setErreur('');
                setF({ code: r.code, libelle: r.libelle || '', description: r.description || '', n: String(r.n), seuil: String(r.seuil), operateur: r.operateur, portee: r.portee, gravite: r.gravite });
              }} title="Corriger la règle" aria-label={`Corriger la règle ${r.code}`}>
                <I n="pen" s={12} />
              </Bouton>
            )}
            <Bouton taille="sm" onClick={() => void run(r.code, () => envoyer({ code: r.code, actif: !r.actif }))} disabled={estOccupe()}>
              {r.actif ? 'Désactiver' : 'Réactiver'}
            </Bouton>
          </div>
        ))}
      </div>

      {ouvert && (
        <div style={{ padding: 14, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', display: 'grid', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10 }}>
            <div><label style={lbl} htmlFor="rg-code">Code *{editionCode && <span style={{ fontWeight: 400, textTransform: 'none' }} title="Le code est cité par les points rejetés : il ne se renumérote pas."> · figé</span>}</label>
              <input id="rg-code" value={f.code} disabled={!!editionCode} onChange={(e) => setF({ ...f, code: e.target.value })}
                placeholder="8-1s" style={{ ...inputStyle, fontFamily: 'var(--font-mono)', opacity: editionCode ? 0.65 : 1 }} /></div>
            <div><label style={lbl} htmlFor="rg-n">Nombre de points *</label>
              <input id="rg-n" value={f.n} onChange={(e) => setF({ ...f, n: e.target.value })}
                inputMode="numeric" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
            <div><label style={lbl} htmlFor="rg-s">Seuil (σ) *</label>
              <input id="rg-s" value={f.seuil} onChange={(e) => setF({ ...f, seuil: e.target.value })}
                inputMode="decimal" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
            <div><label style={lbl} htmlFor="rg-o">Comparaison *</label>
              <select id="rg-o" value={f.operateur} onChange={(e) => setF({ ...f, operateur: e.target.value })} style={inputStyle}>
                {OPERATEURS.map((o) => <option key={o.cle} value={o.cle}>{o.nom}</option>)}
              </select></div>
            <div><label style={lbl} htmlFor="rg-p">Portée</label>
              <select id="rg-p" value={f.portee} onChange={(e) => setF({ ...f, portee: e.target.value })} style={inputStyle}>
                <option value="niveau">Un niveau</option>
                <option value="inter_niveaux">Entre niveaux</option>
              </select></div>
            <div><label style={lbl} htmlFor="rg-g">Conséquence</label>
              <select id="rg-g" value={f.gravite} onChange={(e) => setF({ ...f, gravite: e.target.value })} style={inputStyle}>
                <option value="rejet">Rejet</option>
                <option value="alerte">Avertissement</option>
              </select></div>
          </div>
          <div><label style={lbl} htmlFor="rg-l">Libellé</label>
            <input id="rg-l" value={f.libelle} onChange={(e) => setF({ ...f, libelle: e.target.value })}
              placeholder="8 points consécutifs du même côté au-delà de 1σ" style={inputStyle} /></div>

          <div style={{ fontSize: 12.5, color: 'var(--text-body)', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '8px 11px' }}>
            {aide}
          </div>

          <FieldError>{erreur}</FieldError>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Bouton taille="sm" onClick={() => { setOuvert(false); setEditionCode(null); setErreur(''); }}>Annuler</Bouton>
            <Bouton variante="primaire" taille="sm" onClick={() => void run('c', creer)} disabled={estOccupe()}>
              <I n="check" s={13} />{estOccupe('c') ? 'Enregistrement…' : editionCode ? 'Enregistrer la règle' : 'Créer la règle'}
            </Bouton>
          </div>
        </div>
      )}

      {message && <div style={{ fontSize: 12.5, color: 'var(--green-600)' }}>{message}</div>}
      {!ouvert && erreur && <FieldError>{erreur}</FieldError>}

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Une règle créée ici n’est appliquée à aucune carte tant qu’elle n’y est pas cochée.
        Les règles de Westgard livrées d’origine se désactivent mais ne se suppriment pas :
        l’historique des rejets référence leur code.
      </p>
    </div>
  );
}
